import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import {
  createEvent,
  deleteEvent,
  fetchAdminEvents,
  updateEvent,
  type CalendarEvent,
  type EventPayload,
} from '@/api/eventsApi'
import { useAuth } from '@/auth/AuthContext'
import { AdminHeaderActions } from '@/components/admin/AdminHeaderActions'
import { MarketCalendar } from '@/components/calendar/MarketCalendar'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

const EMPTY_FORM: EventPayload = {
  title: '',
  description: '',
  event_type: 'market',
  event_date: '',
  end_date: '',
  start_time: '08:00',
  end_time: '13:00',
  location: 'Oikos Private School, 0 Pilgrim Street, Barberton',
  is_published: true,
}

function toInputTime(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 5)
}

function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function eventLastDay(event: CalendarEvent): string {
  return event.end_date || event.event_date
}

function compareUpcoming(a: CalendarEvent, b: CalendarEvent): number {
  const byDate = eventLastDay(a).localeCompare(eventLastDay(b))
  if (byDate !== 0) return byDate
  const aTime = a.start_time || ''
  const bTime = b.start_time || ''
  if (aTime !== bTime) return aTime.localeCompare(bTime)
  return a.id - b.id
}

function comparePast(a: CalendarEvent, b: CalendarEvent): number {
  const byDate = eventLastDay(b).localeCompare(eventLastDay(a))
  if (byDate !== 0) return byDate
  return b.id - a.id
}

/**
 * Admin CRUD for market days and town calendar events.
 */
export function AdminEventsPage() {
  const { isAuthenticated, isAdmin, logout } = useAuth()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [form, setForm] = useState<EventPayload>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [showPast, setShowPast] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)

  async function reload() {
    const result = await fetchAdminEvents()
    setEvents(result.events)
  }

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    void (async () => {
      try {
        await reload()
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load events.')
      }
    })()
  }, [isAuthenticated, isAdmin])

  const { upcoming, past } = useMemo(() => {
    const today = todayIso()
    const upcomingEvents = events
      .filter((event) => eventLastDay(event) >= today)
      .sort(compareUpcoming)
    const pastEvents = events
      .filter((event) => eventLastDay(event) < today)
      .sort(comparePast)
    return { upcoming: upcomingEvents, past: pastEvents }
  }, [events])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/vendor" replace />

  function updateField<K extends keyof EventPayload>(key: K, value: EventPayload[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  function startEdit(event: CalendarEvent) {
    setEditingId(event.id)
    setForm({
      title: event.title,
      description: event.description ?? '',
      event_type: event.event_type,
      event_date: event.event_date,
      end_date: event.end_date ?? '',
      start_time: toInputTime(event.start_time),
      end_time: toInputTime(event.end_time),
      location: event.location ?? '',
      is_published: event.is_published,
    })
    setError('')
  }

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    const payload: EventPayload = {
      ...form,
      description: form.description || null,
      end_date: form.end_date || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location || null,
    }
    try {
      if (editingId != null) {
        await updateEvent(editingId, payload)
      } else {
        await createEvent(payload)
      }
      resetForm()
      await reload()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(id: number) {
    if (!window.confirm('Delete this calendar event?')) return
    setBusy(true)
    setError('')
    try {
      await deleteEvent(id)
      if (editingId === id) resetForm()
      await reload()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Delete failed.')
    } finally {
      setBusy(false)
    }
  }

  function renderEventList(items: CalendarEvent[]) {
    if (items.length === 0) {
      return <p className={styles.empty}>No events in this list.</p>
    }
    return items.map((event) => (
      <article key={event.id} className={styles.item}>
        <div>
          <h2 className={styles.itemTitle}>{event.title}</h2>
          <p className={styles.meta}>
            {event.event_type} · {event.event_date}
            {event.end_date ? ` → ${event.end_date}` : ''}
            {event.start_time ? ` · ${toInputTime(event.start_time)}` : ''}
            {event.end_time ? `–${toInputTime(event.end_time)}` : ''}
            {event.is_published ? ' · published' : ' · draft'}
          </p>
          {event.location ? <p className={styles.meta}>{event.location}</p> : null}
          {event.description ? <p className={styles.meta}>{event.description}</p> : null}
        </div>
        <div className={styles.actionCol}>
          <button type="button" className={styles.secondary} disabled={busy} onClick={() => startEdit(event)}>
            Edit
          </button>
          <button type="button" className={styles.ghost} disabled={busy} onClick={() => void onDelete(event.id)}>
            Delete
          </button>
        </div>
      </article>
    ))
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Admin · Calendar</h1>
          <p className={styles.lead}>
            Add market days and other town events. Published entries show on the home calendar.
          </p>
        </div>
        <AdminHeaderActions logout={logout} />
      </header>

      {showCalendar ? (
        <div style={{ marginBottom: '1.5rem' }}>
          <MarketCalendar />
        </div>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}

      <div className={styles.header} style={{ marginBottom: '0.75rem' }}>
        <h2 className={styles.itemTitle}>{editingId != null ? 'Edit event' : 'Manage events'}</h2>
        <button
          type="button"
          className={styles.secondary}
          onClick={() => setShowCalendar((current) => !current)}
        >
          {showCalendar ? 'Hide calendar' : 'View calendar'}
        </button>
      </div>

      <form className={styles.formGrid} onSubmit={onSubmit}>
        <h2 className={styles.itemTitle}>{editingId != null ? 'Edit event' : 'New event'}</h2>

        <label className={authStyles.label}>
          Title
          <input
            className={authStyles.input}
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            required
          />
        </label>

        <label className={authStyles.label}>
          Type
          <select
            className={authStyles.select}
            value={form.event_type}
            onChange={(e) => updateField('event_type', e.target.value as EventPayload['event_type'])}
          >
            <option value="market">Market day</option>
            <option value="town">Town event</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label className={authStyles.label}>
          Date
          <input
            className={authStyles.input}
            type="date"
            value={form.event_date}
            onChange={(e) => updateField('event_date', e.target.value)}
            required
          />
        </label>

        <label className={authStyles.label}>
          End date (optional)
          <input
            className={authStyles.input}
            type="date"
            value={form.end_date ?? ''}
            onChange={(e) => updateField('end_date', e.target.value)}
          />
        </label>

        <label className={authStyles.label}>
          Start time
          <input
            className={authStyles.input}
            type="time"
            value={form.start_time ?? ''}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
        </label>

        <label className={authStyles.label}>
          End time
          <input
            className={authStyles.input}
            type="time"
            value={form.end_time ?? ''}
            onChange={(e) => updateField('end_time', e.target.value)}
          />
        </label>

        <label className={authStyles.label}>
          Location
          <input
            className={authStyles.input}
            value={form.location ?? ''}
            onChange={(e) => updateField('location', e.target.value)}
          />
        </label>

        <label className={authStyles.label}>
          Description
          <textarea
            className={authStyles.textarea}
            value={form.description ?? ''}
            onChange={(e) => updateField('description', e.target.value)}
          />
        </label>

        <label className={styles.check}>
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => updateField('is_published', e.target.checked)}
          />
          Published on public calendar
        </label>

        <div className={styles.actions}>
          <button className={authStyles.submit} type="submit" disabled={busy}>
            {editingId != null ? 'Save changes' : 'Add event'}
          </button>
          {editingId != null ? (
            <button type="button" className={styles.ghost} onClick={resetForm} disabled={busy}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </form>

      <div className={styles.list} style={{ marginTop: '1.5rem' }}>
        <div className={styles.header} style={{ marginBottom: '0.75rem' }}>
          <h2 className={styles.itemTitle}>Upcoming events</h2>
          <button
            type="button"
            className={styles.secondary}
            onClick={() => setShowPast((current) => !current)}
          >
            {showPast ? 'Hide past events' : `List past events (${past.length})`}
          </button>
        </div>

        {upcoming.length === 0 ? (
          <p className={styles.empty}>No upcoming events.</p>
        ) : (
          renderEventList(upcoming)
        )}

        {showPast ? (
          <>
            <h2 className={styles.itemTitle} style={{ marginTop: '1.5rem' }}>
              Past events
            </h2>
            {renderEventList(past)}
          </>
        ) : null}
      </div>
    </section>
  )
}
